import React from 'react';
import { Route } from 'react-router-dom';

import {
  APP_TITLE, HAS_USERS, HOME_ROUTE, OPEN_REGISTRATION, ROUTE_ROLES, PARENT_HOME_ROUTE, PARENT_LOGIN_ROUTE, PARENT_ACCOUNT_ROUTE, PARENT_REPORT_CARD_ROUTE, PARENT_PLAYER_ROUTE,
  STAFF_PATHS,
} from './_configuration';
import { SPORT_TERMINOLOGY } from './app/lib/constants';

// Components
import { RouteByRole } from './app/components/_core/RouteByRole/route-by-role.component';
import { AdminRoute } from './app/components/_core/AdminRoute/admin-route.component';
import { ParentRoute } from './app/components/_core/ParentRoute/parent-route.component';
import { TokenOrParentRoute } from './app/components/_core/TokenOrParentRoute/token-or-parent-route.component';
import { LegacyPublicRedirect } from './app/components/_core/LegacyPublicRedirect/legacy-public-redirect.component';
import { LegacyContactsRedirect } from './app/components/_core/LegacyPublicRedirect/legacy-contacts-redirect.component';
import { DashboardPage } from './app/components/_pages/DashboardPage/dashboard.page';
import { AdminPage } from './app/components/_pages/AdminPage/admin.page';
import { ManageUsersPage } from './app/components/_pages/ManageUsersPage/manage-users.page';
import { LoginPage } from './app/components/_pages/_auth/LoginPage/login.page';
import { ParentLoginPage } from './app/components/_pages/_auth/ParentLoginPage/parent-login.page';
import { ParentDashboardPage } from './app/components/_pages/ParentDashboardPage/parent-dashboard.page';
import { ParentAccountPage } from './app/components/_pages/ParentAccountPage/parent-account.page';
import { ForgotPasswordPage } from './app/components/_pages/_auth/ForgotPasswordPage/forgot-password.page';
import { ResetPasswordPage } from './app/components/_pages/_auth/ResetPasswordPage/reset-password.page';
import { VerifyAccountPage } from './app/components/_pages/_auth/VerifyAccountPage/verify-account.page';
import RegisterPageContainer from './app/components/_pages/_auth/RegisterPage/register.page';
import AcceptInvitePage from './app/components/_pages/_auth/AcceptInvitePage/accept-invite.page';
import { ManageDrillsPage } from './app/components/_pages/ManageDrillsPage/manage-drills.page';
import { ManagePlayersPage } from './app/components/_pages/ManagePlayersPage/manage-players.page';
import ManageSessionPage from './app/components/_pages/ManageSessionsPage/manage-sessions.page';
import { CreateAssessmentPage } from './app/components/_pages/CreateAssessmentPage/create-assessment.page';
import { UpcomingAssessmentSessionsPage }
  from './app/components/_pages/UpcomingAssessmentSessions/upcoming-assessment-sessions.page';
import ManageSkillsPage from './app/components/_pages/ManageSkillsPage/manage-skills.page';
import ManagePracticePlanPage from './app/components/_pages/ManagePracticePlansPage/manage-practice-plan.page';
import { EditPlayerPage } from './app/components/_pages/EditPlayerPage/edit-player.page';
import EditAssessmentSessionPage from './app/components/_pages/EditAssessmentSessionPage/edit-assessment-session.page';
import { ConfigureSchedulePage } from './app/components/_pages/ConfigureSchedulePage/configure-schedule.page';
import { RequestEmailsPage } from './app/components/_pages/RequestEmailsPage/request-emails.page';
import { ManageWeightingPage } from './app/components/_pages/ManageWeightingPage/manage-weighting.page';
import { ManageAgeGroupPage } from './app/components/_pages/ManageAgeGroup/manage-age-group.page';
import CheckInPage from './app/components/_pages/CheckInPage/check-in.page';
import { EvaluatePage } from './app/components/_pages/EvaluatePage/evaluate.page';
import { MyAssignmentsPage } from './app/components/_pages/MyAssignmentsPage/my-assignments.page';
import { AssignmentScoringPage } from './app/components/_pages/MyAssignmentsPage/assignment-scoring.page';
import EditDrillPage from './app/components/_pages/EditDrillPage/edit-drill.page';
import { ReportsPage } from './app/components/_pages/ReportsPage/reports.page';
import { ReportCardPage } from './app/components/_pages/ReportCardPage/report-card.page';
import { PublicEditPlayerPage } from './app/components/_pages/PublicEditPlayerPage/public-edit-player.page';
import { SettingsPage } from './app/components/_pages/SettingsPage/settings.page';
import { NotFoundPage } from './app/components/_pages/_boilerplate/NotFoundPage/not-found-page.component';
import { Sport, SportTerminology } from './app/lib/types';
import { pluralTerm } from './app/lib/helpers/term.helper';

const defaultSportTerms = SPORT_TERMINOLOGY[Sport.default];
const manageEventsTitle = `${APP_TITLE} - Manage ${
  pluralTerm(defaultSportTerms[SportTerminology.EVENT])
}`;
const defaultSessionTerm = defaultSportTerms[SportTerminology.SESSION];

export const STAFF_ROUTES = [
  <RouteByRole key="upcoming" exact title={`${APP_TITLE} - Upcoming Evaluations`} path="/upcoming" componentsByRole={{
    [ROUTE_ROLES.CHECK_IN]: UpcomingAssessmentSessionsPage,
    [ROUTE_ROLES.SCORING]: UpcomingAssessmentSessionsPage
  }} />,
  <RouteByRole key="drills" exact title={manageEventsTitle} path={STAFF_PATHS.DRILLS} componentsByRole={{
    [ROUTE_ROLES.CONFIG_DRILLS]: ManageDrillsPage
  }} />,
  <RouteByRole key="drills" exact title={manageEventsTitle} path={STAFF_PATHS.DRILL} componentsByRole={{
    [ROUTE_ROLES.CONFIG_DRILLS]: EditDrillPage
  }} />,
  <RouteByRole key="skills" exact title={manageEventsTitle} path={`/${pluralTerm(defaultSportTerms[SportTerminology.METRIC].toLowerCase())}`} componentsByRole={{
    [ROUTE_ROLES.CONFIG_DRILLS]: ManageSkillsPage
  }} />,
  <RouteByRole key="practice-plan" exact title={manageEventsTitle} path="/practice-plans" componentsByRole={{
    [ROUTE_ROLES.CONFIG_DRILLS]: ManagePracticePlanPage
  }} />,
  <RouteByRole key="players" exact title={`${APP_TITLE} - Manage ${pluralTerm(defaultSportTerms[SportTerminology.ATHLETE])}`} path={STAFF_PATHS.PLAYERS} componentsByRole={{
    [ROUTE_ROLES.CONFIGURATION]: ManagePlayersPage,
    [ROUTE_ROLES.REPORTS]: ManagePlayersPage
  }} />,
  <RouteByRole key="edit-player" exact title={`${APP_TITLE} - Edit ${defaultSportTerms[SportTerminology.ATHLETE]}`} path={STAFF_PATHS.PLAYER_ASSESSMENT} componentsByRole={{
    [ROUTE_ROLES.CONFIGURATION]: EditPlayerPage,
    [ROUTE_ROLES.REPORTS]: EditPlayerPage
  }} />,
  <RouteByRole key="sessions" exact title={`${APP_TITLE} - Manage ${pluralTerm(defaultSessionTerm)}`} path={STAFF_PATHS.SESSIONS} componentsByRole={{
    [ROUTE_ROLES.CONFIGURATION]: ManageSessionPage
  }} />,
  <RouteByRole key="edit-sessions" exact title={`${APP_TITLE} - Edit ${defaultSessionTerm}`} path={STAFF_PATHS.ASSESSMENT_SESSION} componentsByRole={{
    [ROUTE_ROLES.CONFIGURATION]: EditAssessmentSessionPage
  }} />,
  <RouteByRole key="create-assessment" exact title={`${APP_TITLE} - Create Assessment`} path="/assessments/create" componentsByRole={{
    [ROUTE_ROLES.AUTHORIZED]: CreateAssessmentPage
  }} />,
  <RouteByRole key="configure-schedule" exact title={`${APP_TITLE} - Configure Schedule Format`} path="/format" componentsByRole={{
    [ROUTE_ROLES.CONFIGURATION]: ConfigureSchedulePage
  }} />,
  <RouteByRole key="manage-age-group" exact title={`${APP_TITLE} - Manage Age Group`} path={STAFF_PATHS.AGE_GROUP} componentsByRole={{
    [ROUTE_ROLES.CONFIGURATION]: ManageAgeGroupPage
  }} />,
  <RouteByRole key="configure-weighting" exact title={`${APP_TITLE} - Configure Weightings`} path="/weighting" componentsByRole={{
    [ROUTE_ROLES.CONFIGURATION]: ManageWeightingPage
  }} />,
  <RouteByRole key="request-email" exact title={`${APP_TITLE} - Request Emails`} path="/request-emails" component={RequestEmailsPage} />,
  <RouteByRole key="request-email" exact title={`${APP_TITLE} - Request Emails`} path="/request-email" component={RequestEmailsPage} />,
  // The code-gated public contacts page is retired; already-sent welcome
  // email/SMS links forward into the parent app's contact-details section.
  <Route key="legacy-manage-contacts" exact path={`/${defaultSportTerms[SportTerminology.ATHLETE].toLowerCase()}/:code/contacts`}>
    <LegacyContactsRedirect />
  </Route>,
  <Route key="legacy-manage-contacts-short" exact path={`${STAFF_PATHS.PLAYERS}/:code`}>
    <LegacyContactsRedirect />
  </Route>,
  <RouteByRole key="check-in" exact title={`${APP_TITLE} - Check-In`} path={STAFF_PATHS.CHECK_IN} componentsByRole={{
    [ROUTE_ROLES.CHECK_IN]: CheckInPage
  }}  />,
  <RouteByRole key="evaluate" exact title={`${APP_TITLE} - Evaluate`} path={STAFF_PATHS.EVALUATE} componentsByRole={{
    [ROUTE_ROLES.SCORING]: EvaluatePage
  }}  />,
  <RouteByRole key="assignments" exact title={`${APP_TITLE} - Assignments`} path={STAFF_PATHS.ASSIGNMENTS} componentsByRole={{
    [ROUTE_ROLES.SCORING]: MyAssignmentsPage
  }} />,
  <RouteByRole key="assignment-scoring" exact title={`${APP_TITLE} - Assignment`} path={STAFF_PATHS.ASSIGNMENT} componentsByRole={{
    [ROUTE_ROLES.SCORING]: AssignmentScoringPage
  }} />,
  <RouteByRole key="reports" exact title={`${APP_TITLE} - Reports`} path={STAFF_PATHS.AGE_GROUP_REPORTS} componentsByRole={{
    [ROUTE_ROLES.REPORTS]: ReportsPage
  }}  />,
  <RouteByRole key="report-cards" exact title={`${APP_TITLE} - Report Card`} path={STAFF_PATHS.REPORT_CARD} component={ReportCardPage} />,
  <Route key="legacy-public-report-card" exact path="/public/report-card/:code/:playerAssessmentId">
    <LegacyPublicRedirect base={PARENT_REPORT_CARD_ROUTE} />
  </Route>,
  <Route key="legacy-public-edit-player" exact path="/public/edit-player/:code/:playerAssessmentId">
    <LegacyPublicRedirect base={PARENT_PLAYER_ROUTE} />
  </Route>,
  <RouteByRole key="settings" exact title={`${APP_TITLE} - Settings`} path="/settings" componentsByRole={{
    [ROUTE_ROLES.CONFIGURATION]: SettingsPage
  }}/>,
  <AdminRoute key="admin" exact title={`${APP_TITLE} - Admin`} path="/admin" component={AdminPage} />,
  <RouteByRole key="home" exact title={APP_TITLE} path="/" componentsByRole={{
    [ROUTE_ROLES.REPORTS]: DashboardPage,
    [ROUTE_ROLES.CONFIGURATION]: DashboardPage,
    [ROUTE_ROLES.CHECK_IN]: UpcomingAssessmentSessionsPage,
    [ROUTE_ROLES.SCORING]: UpcomingAssessmentSessionsPage,
    [ROUTE_ROLES.USER_MANAGEMENT]: ManageUsersPage,
    [ROUTE_ROLES.AUTHORIZED]: DashboardPage
  }} />,
  HAS_USERS && <RouteByRole key="login" exact title={`${APP_TITLE} - Sign In`} path="/login" componentsByRole={{
    [ROUTE_ROLES.PUBLIC]: LoginPage
  }} />,
  HAS_USERS && <RouteByRole key="forgot-password" title={`${APP_TITLE} - Forgot Password`} exact path="/forgot-password" componentsByRole={{
    [ROUTE_ROLES.PUBLIC]: ForgotPasswordPage
  }} />,
  HAS_USERS && <RouteByRole key="password-reset" exact title={`${APP_TITLE} - Reset Password`} path="/password-reset" component={ResetPasswordPage} />,
  HAS_USERS && <RouteByRole key="users" exact title={`${APP_TITLE} - Manage Users`} path="/users" componentsByRole={{
    [ROUTE_ROLES.USER_MANAGEMENT]: ManageUsersPage
  }} />,
  OPEN_REGISTRATION && <RouteByRole key="join" exact title={`${APP_TITLE}  - Join`} path="/join" component={AcceptInvitePage} />,
  OPEN_REGISTRATION && <RouteByRole key="register" exact title={`${APP_TITLE}  - Register`} path="/register" component={RegisterPageContainer} />,
  HAS_USERS && <RouteByRole key="verify" title={`Verify Account - ${APP_TITLE}`} chat exact path="/verify" component={VerifyAccountPage} />,
  // Catch-all: must be last; an unconstrained <Route> matches every unmatched
  // URL, and Switch picks the first match. Home target is the staff dashboard.
  <Route key="staff-not-found" render={() => <NotFoundPage homeTo={HOME_ROUTE} />} />
];

export const PARENT_ROUTES = [
  <Route key="parent-login" exact path={PARENT_LOGIN_ROUTE} component={ParentLoginPage} />,
  <ParentRoute key="parent-home" exact title={`${APP_TITLE} - My Players`} path={PARENT_HOME_ROUTE} component={ParentDashboardPage} />,
  <ParentRoute key="parent-account" exact title={`${APP_TITLE} - My Account`} path={PARENT_ACCOUNT_ROUTE} component={ParentAccountPage} />,
  // Token-OR-parent gated: a `?token=` URL param (anonymous email/SMS link) or a
  // parent JWT. TokenOrParentRoute redirects to login when neither is present.
  <TokenOrParentRoute key="parent-report-card" exact title={`${APP_TITLE} - Report Card`} path={`${PARENT_REPORT_CARD_ROUTE}/:code/:playerAssessmentId`} component={ReportCardPage} />,
  <TokenOrParentRoute key="parent-player" exact title={`${APP_TITLE} - Player Details`} path={`${PARENT_PLAYER_ROUTE}/:code/:playerAssessmentId`} component={PublicEditPlayerPage} />,
  // Catch-all (see STAFF_ROUTES). Home target is the parent dashboard, so the
  // user lands back in /parent rather than being kicked over to the staff app.
  <Route key="parent-not-found" render={() => <NotFoundPage homeTo={PARENT_HOME_ROUTE} />} />
];
